import getConfig from "next/config";
import setCookieParser, { splitCookiesString } from "set-cookie-parser";
import { serialize, parse } from "cookie";
import * as cookie from "cookie";
import { GetServerSidePropsContext, NextPageContext } from "next";
import { ExtendedNextPageContext } from "types/app";
import { runInBrowserOnly } from "helpers/common";
import { STORAGE_PREFIX } from "constants/variables";

type ParsedCookie = {
  name: string;
  value: string | number | Record<string, any>;
  path?: string;
  domain?: string;
  maxAge?: number;
};

const { publicRuntimeConfig } = getConfig();

const PRODUCT_HISTORY_COOKIE_NAME = `${STORAGE_PREFIX}v2_product_history`;

export const getBrowsingHistoryIds = (): Array<Entity.ID> => {
  return (getClientCookie(PRODUCT_HISTORY_COOKIE_NAME) ||
    []) as Array<Entity.ID>;
};

export function saveBrowsingHistory(id: Entity.ID): void {
  if (!id) return;

  const current = getBrowsingHistoryIds();

  if (!current.includes(id)) {
    current.unshift(id);
    setClientCookie(
      PRODUCT_HISTORY_COOKIE_NAME,
      JSON.stringify(current.slice(0, 10))
    );
  }
}

export function getHoursMaxAgeCookie(hours = 1): number {
  return hours * 60 * 60;
}

export function getDaysMaxAgeCookie(days = 1): number {
  return days * 24 * 60 * 60;
}

export const getClientCookie = runInBrowserOnly<Common.UnionCookie>(
  (name: string): Common.UnionCookie => {
    const parsedCookies = parse(window.document.cookie);
    const foundCookieKey = Object.keys(parsedCookies).find(cookie_name => {
      return cookie_name === name;
    });

    if (parsedCookies[foundCookieKey]) {
      try {
        return JSON.parse(parsedCookies[foundCookieKey]);
      } catch (e) {
        return parsedCookies[foundCookieKey];
      }
    }
  }
);

export const setClientCookie = runInBrowserOnly<void>(
  (name: string, value, options = {}) => {
    if (!options.path) {
      options.path = "/";
    }
    // если объект то нужно использовать JSON.stringify
    const valueChecked = isObject(value) ? JSON.stringify(value) : value;

    window.document.cookie = cookie.serialize(name, valueChecked, options);
  }
);

export const removeClientCookie = runInBrowserOnly<void>(
  (name: string, options = {}) => {
    const { domain, path } = options;
    return setClientCookie(name, "", {
      domain,
      maxAge: -1,
      path,
    });
  }
);

export function getServerRequestCookie(
  nextContext:
    | NextPageContext
    | ExtendedNextPageContext
    | GetServerSidePropsContext,
  cookieName: string
): Common.UnionCookie | boolean | null {
  if (nextContext?.req.headers.cookie) {
    const parsedCookies = parse(nextContext.req.headers.cookie);
    const foundCookieKey = Object.keys(parsedCookies).find(cookie_name => {
      return cookie_name === cookieName;
    });

    try {
      if (foundCookieKey) {
        return JSON.parse(parsedCookies[foundCookieKey]);
      }
    } catch (e) {
      return parsedCookies[foundCookieKey];
    }
  }
  return null;
}

// для проверки если не объект то не нужно вызывать JSON.stringify
const isObject = value => typeof value === "object" && value !== null;

export function appendServerResponseCookie(
  nextContext: Pick<ExtendedNextPageContext, "req" | "res">,
  cookies: ParsedCookie | Array<ParsedCookie>
): void {
  if (nextContext?.res && !nextContext.res.writableEnded) {
    try {
      let newCookies = !Array.isArray(cookies) ? [cookies] : cookies;
      let currentCookie = setCookieParser(
        splitCookiesString(nextContext.res.getHeader("Set-Cookie"))
      );
      //todo: может быть можно сделать без лишнего перебора
      const newCookiesNames = newCookies.map(c => {
        return c.name;
      });

      currentCookie = currentCookie
        .filter(c => {
          return !newCookiesNames.includes(c.name);
        })
        .map((cookie: ParsedCookie) => {
          const { name, value, ...options } = cookie;
          return serialize(name, value, options);
        });

      newCookies = newCookies.map(cookie => {
        const { name, value, ...options } = cookie;
        // если объект то нужно использовать JSON.stringify
        const valueChecked = isObject(value) ? JSON.stringify(value) : value;

        return serialize(name, valueChecked, {
          ...options,
          path: options.path || "/",
          domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN
            ? publicRuntimeConfig.COOKIE_FORWARD_DOMAIN
            : options.domain,
        });
      });

      nextContext.res.setHeader("Set-Cookie", [
        ...currentCookie,
        ...newCookies,
      ]);
    } catch (e) {
      console.error("appendNextResponseCookie:", e);
    }
  }
}

export function getExperimentCondition(
  name: string,
  strict = true
): Common.UnionCookie {
  if (typeof window === "undefined") return false;

  const cookieValue = getClientCookie(name);

  return strict ? cookieValue === true : cookieValue;
}
